'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  detectWallet,
  ensureAuthenticated,
  friendlyPaymentError,
  getIdentityKey,
  getNetwork,
  payIdentityKey,
} from './wallet';
import { recordPayment, flushPaymentQueue } from './payment-queue';

type Status = 'disconnected' | 'connecting' | 'connected';

export type PayResult = {
  ok: boolean;
  reason?: 'busy' | 'not-connected' | 'no-recipient' | 'no-payer' | 'network' | 'error';
  /** Human-readable failure detail (the wallet's own error message when available). */
  error?: string;
  txid?: string;
};

export type PayOptions = {
  satoshis: number;
  /** Human-readable description shown in the wallet's approval UI. */
  description: string;
  /** API route the claim record is delivered to, e.g. "/api/crystals/purchase". */
  endpoint: string;
  /** What the payment was for, e.g. "crystals" | "ladder-entry". */
  purpose: string;
  /** Optional purpose-specific payload forwarded with the claim record. */
  extra?: Record<string, unknown>;
};

interface WalletContextValue {
  status: Status;
  identityKey: string | null;
  network: string | null;
  /** Platform (recipient) identity pubkey, served by /api/config. */
  platformKey: string | null;
  paying: boolean;
  lastError: string | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  /** Pay the platform identity key and durably deliver the claim record. */
  pay: (opts: PayOptions) => Promise<PayResult>;
}

const WalletContext = createContext<WalletContextValue | null>(null);

export function useWallet(): WalletContextValue {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error('useWallet must be used within a WalletProvider');
  return ctx;
}

export function WalletProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<Status>('disconnected');
  const [identityKey, setIdentityKey] = useState<string | null>(null);
  const [network, setNetwork] = useState<string | null>(null);
  const [platformKey, setPlatformKey] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  // Refs mirror async-critical values so pay() never reads a stale closure.
  const connectedRef = useRef(false);
  const payingRef = useRef(false);
  const networkRef = useRef<string | null>(null);
  const identityKeyRef = useRef<string | null>(null);
  const platformKeyRef = useRef<string | null>(null);

  // Fetch the platform identity pubkey (server-provided, public). Returns the
  // key or null; callers can re-invoke this lazily so a failed/raced initial
  // load doesn't permanently block paying.
  const loadPlatformKey = useCallback(async (): Promise<string | null> => {
    if (platformKeyRef.current) return platformKeyRef.current;
    try {
      const r = await fetch('/api/config');
      const d = await r.json();
      if (d.platformKey) {
        platformKeyRef.current = d.platformKey as string;
        setPlatformKey(platformKeyRef.current);
        return platformKeyRef.current;
      }
      setLastError(
        d.error ?? 'Platform identity key is not configured on the server.'
      );
      return null;
    } catch (e) {
      setLastError(e instanceof Error ? e.message : String(e));
      return null;
    }
  }, []);

  // Load once on mount, retrying briefly with backoff so a transient failure
  // doesn't leave the platform key permanently unset.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (let attempt = 0; attempt < 3 && !cancelled; attempt++) {
        const key = await loadPlatformKey();
        if (key) return;
        await new Promise(res => setTimeout(res, 1000 * (attempt + 1)));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadPlatformKey]);

  // Retry any claim records stranded by a failed POST (offline, crash, closed
  // tab) — on mount and whenever connectivity returns.
  useEffect(() => {
    void flushPaymentQueue();
    const onOnline = () => void flushPaymentQueue();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  const connect = useCallback(async () => {
    setStatus('connecting');
    setLastError(null);

    const available = await detectWallet();
    if (!available) {
      setStatus('disconnected');
      setLastError('No BSV wallet found. Install a BRC-100 wallet.');
      return;
    }

    const ok = await ensureAuthenticated();
    if (!ok) {
      setStatus('disconnected');
      setLastError('Wallet authentication was cancelled.');
      return;
    }

    try {
      const id = await getIdentityKey();
      identityKeyRef.current = id;
      setIdentityKey(id);

      // Tolerate getNetwork failure at connect (some wallets may not expose
      // it) but store the result — pay() fails closed on anything that isn't
      // a confirmed 'mainnet'.
      let net: string | null = null;
      try {
        net = await getNetwork();
      } catch {
        /* some wallets may not expose it */
      }
      networkRef.current = net;
      setNetwork(net);
      if (net && net !== 'mainnet') {
        setLastError(
          `Wallet is on ${net}; payments target mainnet. Switch your wallet to mainnet.`
        );
      }

      connectedRef.current = true;
      setStatus('connected');
    } catch (e) {
      connectedRef.current = false;
      setStatus('disconnected');
      setLastError(e instanceof Error ? e.message : 'Failed to connect wallet.');
    }
  }, []);

  const disconnect = useCallback(() => {
    connectedRef.current = false;
    networkRef.current = null;
    identityKeyRef.current = null;
    setStatus('disconnected');
    setIdentityKey(null);
    setNetwork(null);
    setLastError(null);
  }, []);

  const pay = useCallback(
    async (opts: PayOptions): Promise<PayResult> => {
      if (payingRef.current) return { ok: false, reason: 'busy', error: 'A payment is already in progress.' };
      if (!connectedRef.current) {
        setLastError('Connect a wallet before paying.');
        return { ok: false, reason: 'not-connected', error: 'Connect a wallet before paying.' };
      }

      // Lazily (re)load the platform key if the initial fetch failed or raced.
      const recipient = platformKeyRef.current ?? (await loadPlatformKey());
      const payer = identityKeyRef.current;
      if (!recipient) {
        const msg = 'Payment recipient unavailable — the server is missing PLATFORM_IDENTITY_KEY.';
        setLastError(msg);
        return { ok: false, reason: 'no-recipient', error: msg };
      }
      if (!payer) {
        const msg = 'Wallet identity unavailable — reconnect your wallet.';
        setLastError(msg);
        return { ok: false, reason: 'no-payer', error: msg };
      }

      // Fail closed: mainnet addresses must never be paid from a testnet or
      // unknown wallet. A missing network reading blocks rather than silently
      // proceeding.
      if (networkRef.current !== 'mainnet') {
        const msg = networkRef.current
          ? `Wallet is on ${networkRef.current}; switch to mainnet to pay.`
          : 'Wallet network could not be confirmed; switch to mainnet to pay.';
        setLastError(msg);
        return { ok: false, reason: 'network', error: msg };
      }

      payingRef.current = true;
      setPaying(true);
      try {
        const { txid, tx, derivationPrefix, derivationSuffix } =
          await payIdentityKey(recipient, opts.satoshis, opts.description);
        setLastError(null);

        // Durable claim delivery: persisted + retried until acknowledged.
        // `tx` (AtomicBEEF) rides along so the server wallet can SPV-verify
        // and internalize the payment.
        void recordPayment({
          endpoint: opts.endpoint,
          txid,
          satoshis: opts.satoshis,
          payerKey: payer,
          derivationPrefix,
          derivationSuffix,
          tx,
          purpose: opts.purpose,
          ...(opts.extra ? { extra: opts.extra } : {}),
        });

        return { ok: true, txid };
      } catch (e) {
        // Log the full error - wallet SDK errors carry useful context beyond
        // the message (code, status) that helps diagnose payment failures
        console.error('[wallet] createAction failed:', e);
        // Users see a short readable message, not the SDK's internal dump
        const msg = friendlyPaymentError(e);
        setLastError(msg);
        return { ok: false, reason: 'error', error: msg };
      } finally {
        payingRef.current = false;
        setPaying(false);
      }
    },
    [loadPlatformKey]
  );

  return (
    <WalletContext.Provider
      value={{
        status,
        identityKey,
        network,
        platformKey,
        paying,
        lastError,
        connect,
        disconnect,
        pay,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}
